import type { ComponentType } from 'react';
import { Hueco } from '../../componentes/EstadoPagina';
import type { NombreIcono } from '../../componentes/iconos';
import { Segmentos } from '../../componentes/Segmentos';
import { dibujoDe } from '../../marco/iconosMenu';
import type { PropsPantalla } from '../tipos';
import { Asignacion } from './asignacion/Asignacion';
import { Conciliacion } from './conciliacion/Conciliacion';
import { Cuenta } from './cuenta/Cuenta';
import { Disputas } from './disputas/Disputas';
import { ListaPagos } from './lista/ListaPagos';
import { Politicas } from './politicas/Politicas';
import '../../componentes/apartado.css';
import './pagos.css';

/**
 * Pantalla «Pagos» (4.1 a 4.8) con su barra de apartados (RV Segmentos): Pagos · Disputas · Conciliación · Cuenta de
 * cobro · Políticas · Dónde se aplican. La entrada madre `pagos` no tiene contenido propio: los apartados los declara el
 * servidor (G130, G141, G276) con `parent_key: pagos` y `nested_as: section`, en su orden y solo si la persona tiene su
 * permiso; cada uno va en `?apartado=` (la ruta de su entrada). Sin `?apartado=`, el primero que llega. Las únicas
 * cifras son las `badge` del servidor (reembolsos de Stripe por decidir y disputas abiertas), también en móvil.
 */
const APARTADOS_PAGOS: Readonly<Partial<Record<string, ComponentType<PropsPantalla>>>> = {
    pagos_pagos: ListaPagos,
    pagos_disputas: Disputas,
    pagos_conciliacion: Conciliacion,
    pagos_cuenta: Cuenta,
    pagos_politicas: Politicas,
    pagos_asignacion: Asignacion,
};

const CORTAS: Readonly<Record<string, string>> = {
    pagos_conciliacion: 'Conciliar',
    pagos_cuenta: 'Cuenta',
    pagos_asignacion: 'Asignar',
};

interface Apartado {
    clave: string;
    etiqueta: string;
    corta?: string;
    icono: NombreIcono;
    valor: string;
    ruta: string;
    cifra: number | null;
}

/** El `?apartado=` de la ruta de una entrada anidada (`/pagos?apartado=disputas`); sin él, su clave sin `pagos_`. */
function valorDe(clave: string, ruta: string): string {
    return new URLSearchParams(ruta.split('?')[1] ?? '').get('apartado') ?? clave.replace(/^pagos_/, '');
}

export function Pagos(props: PropsPantalla) {
    const { busqueda, ruta, navegar, movil } = props;
    const apartados: Apartado[] = (props.anidadas ?? [])
        .filter((e) => e.anidadaComo === 'section' && !e.noContratada)
        .map((e) => ({ clave: e.clave, etiqueta: e.etiqueta, corta: CORTAS[e.clave], icono: dibujoDe(e.icono), valor: valorDe(e.clave, e.ruta), ruta: e.ruta, cifra: e.contador }));
    const pedido = busqueda.get('apartado');
    const activo = apartados.find((a) => a.valor === pedido) ?? apartados[0];

    if (activo === undefined) {
        return <Hueco seccion="Pagos" icono="credit-card" />;
    }
    const indice = apartados.indexOf(activo);
    const Apartado = APARTADOS_PAGOS[activo.clave];

    return (
        <div className="rv-pg-pantalla">
            {apartados.length > 1 && (
                <div className="rv-pg-barra">
                    <Segmentos
                        etiqueta="Apartados de pagos"
                        movil={movil}
                        items={apartados.map((a) => ({ etiqueta: a.etiqueta, corta: a.corta, icono: a.icono, cifra: a.cifra }))}
                        valor={indice}
                        onCambio={(i) => {
                            const a = apartados[i];
                            if (a !== undefined) {
                                navegar(a.ruta.includes('?') ? a.ruta : `${ruta}?apartado=${encodeURIComponent(a.valor)}`);
                            }
                        }}
                    />
                </div>
            )}
            {Apartado === undefined ? <Hueco seccion={activo.etiqueta} icono={activo.icono} /> : <Apartado key={activo.clave} {...props} />}
        </div>
    );
}
